import { useCallback, useMemo } from "react";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { ConvexProviderWithAuth, ConvexReactClient } from "convex/react";
import type { ReactNode } from "react";
import {
  getAuthKitSessionMode,
  getConvexUrl,
  getWorkOsClientId,
  getWorkOsRedirectUri,
  hasWorkOsConfig,
} from "../lib/config";

// Always mount a provider so hooks like useQuery(..., "skip") are safe without credentials.
const convex = new ConvexReactClient(getConvexUrl() ?? "https://placeholder.convex.cloud");
const workosClientId = getWorkOsClientId();
const workosRedirectUri = getWorkOsRedirectUri();
const authKitSession = getAuthKitSessionMode();

function useAuthFromAuthKit() {
  const { isLoading, user, getAccessToken } = useAuth();
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        const token = await getAccessToken({ forceRefresh: forceRefreshToken });
        return token ?? null;
      } catch {
        return null;
      }
    },
    [getAccessToken],
  );

  return useMemo(
    () => ({
      isLoading,
      isAuthenticated: !!user,
      fetchAccessToken,
    }),
    [isLoading, user, fetchAccessToken],
  );
}

function useUnauthenticatedConvexAuth() {
  return {
    isLoading: false,
    isAuthenticated: false,
    fetchAccessToken: async () => null,
  };
}

function ConvexTree({ children }: { children: ReactNode }) {
  return (
    <ConvexProviderWithAuth
      client={convex}
      useAuth={hasWorkOsConfig ? useAuthFromAuthKit : useUnauthenticatedConvexAuth}
    >
      {children}
    </ConvexProviderWithAuth>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  if (!hasWorkOsConfig) {
    return <ConvexTree>{children}</ConvexTree>;
  }

  return (
    <AuthKitProvider
      clientId={workosClientId!}
      redirectUri={workosRedirectUri}
      apiHostname={authKitSession.kind === "cookie" ? authKitSession.apiHostname : undefined}
      devMode={authKitSession.devMode}
    >
      <ConvexTree>{children}</ConvexTree>
    </AuthKitProvider>
  );
}
